import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitWeeklyCheckin } from "../../lib/weekly";

// Stage 2C — guided one-question-at-a-time Weekly Check-In.
// Questions 1–7 come from the server (exact prompts for the client's rotation
// position). Question 8 is permanent with three structured fields.

const TOTAL = 8;
const hasText = (v) => typeof v === "string" && v.trim().length > 0;

export default function CheckinForm({ status, onCompleted }) {
  const { questions, question_8: q8, window: win, rotation_position, reflection_message } = status;
  const [step, setStep] = useState(0); // 0..7
  const [answers, setAnswers] = useState(() => Array(7).fill(""));
  const [q8Answers, setQ8Answers] = useState({ physical_win: "", habit_win: "", identity_reflection: "" });
  const [busy, setBusy] = useState(false);

  const isLast = step === TOTAL - 1;
  const canNext = isLast
    ? q8.fields.every((f) => hasText(q8Answers[f.key]))
    : hasText(answers[step]);

  const submit = async () => {
    if (!canNext || busy) return;
    setBusy(true);
    try {
      const r = await submitWeeklyCheckin({
        weekly_window_identifier: win.identifier,
        rotation_position,
        answers: answers.map((a) => a.trim()),
        question_8: Object.fromEntries(Object.entries(q8Answers).map(([k, v]) => [k, v.trim()])),
      });
      await onCompleted(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't complete your check-in. Please try again.");
      setBusy(false);
      if (err?.response?.status === 409) onCompleted(null, true);
    }
  };

  const current = isLast ? null : questions[step];

  return (
    <section className="mt-10" data-testid="weekly-checkin-form">
      <div className="rounded-xl border-2 border-red-500 bg-red-50 p-4 md:p-5 mb-6" role="alert" data-testid="weekly-important-reminder">
        <p className="text-xs font-bold tracking-[0.18em] uppercase text-red-700" data-testid="weekly-important-reminder-heading">Important Reminder</p>
        <p className="mt-2 text-sm md:text-base text-red-800 leading-relaxed font-medium" data-testid="weekly-important-reminder-text">
          Please remember before or after this check in to update your measurements &amp; weight. It is crucial we have weekly data to see your progress not just shooting arrows in the dark. You can update your weight &amp; measurements in Everfit. For progress photos do every 6-8 weeks
        </p>
      </div>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="label-eyebrow flex items-center gap-3" data-testid="weekly-progress-label">
          <span className="inline-block h-px w-10 bg-brand-primary" />
          Question {step + 1} of {TOTAL}
        </div>
      </div>
      <div className="mt-4 h-1 w-full rounded-full bg-brand-ink/10 overflow-hidden" aria-hidden>
        <div className="h-full bg-brand-primary transition-[width] duration-500" style={{ width: `${((step + 1) / TOTAL) * 100}%` }} data-testid="weekly-progress-bar" />
      </div>

      <p className="mt-8 text-sm md:text-base text-brand-ink-muted leading-relaxed max-w-2xl italic" data-testid="weekly-reflection-message">
        {reflection_message}
      </p>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -16 }}
          transition={{ duration: 0.3 }}
          className="mt-6"
        >
          {!isLast ? (
            <div data-testid={`weekly-question-${step + 1}`}>
              <p className="font-serif text-xl md:text-2xl text-brand-ink leading-snug" data-testid="weekly-question-prompt">{current.prompt}</p>
              <textarea
                className="input-warm mt-5 text-base leading-relaxed min-h-[200px]"
                placeholder="Take a moment, then write your reflection here…"
                value={answers[step]}
                onChange={(e) => setAnswers((a) => a.map((v, i) => (i === step ? e.target.value : v)))}
                data-testid="weekly-answer-input"
                autoFocus
              />
            </div>
          ) : (
            <div data-testid="weekly-question-8">
              <p className="font-serif text-xl md:text-2xl text-brand-ink leading-snug" data-testid="weekly-question-prompt">{q8.prompt}</p>
              <div className="mt-6 space-y-6">
                {q8.fields.map((f, i) => (
                  <div key={f.key}>
                    <label className="label-eyebrow block mb-2" htmlFor={`q8-${f.key}`}>{f.label}</label>
                    <textarea
                      id={`q8-${f.key}`}
                      className="input-warm text-base leading-relaxed min-h-[110px]"
                      value={q8Answers[f.key]}
                      onChange={(e) => setQ8Answers((s) => ({ ...s, [f.key]: e.target.value }))}
                      data-testid={`weekly-q8-${f.key.replace(/_/g, "-")}-input`}
                      autoFocus={i === 0}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-8 flex items-center justify-between gap-3">
        {step > 0 ? (
          <button type="button" onClick={() => setStep((s) => s - 1)} disabled={busy}
            className="btn-ghost border border-brand-border rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-bold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="weekly-back-btn">
            <ArrowLeft size={14} strokeWidth={2} /> Back
          </button>
        ) : <span />}
        {isLast ? (
          <button type="button" onClick={submit} disabled={!canNext || busy}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="weekly-complete-btn">
            {busy ? <><Loader2 size={18} className="animate-spin" /> Completing…</> : <><CheckCircle2 size={18} strokeWidth={2} /> Complete Check In</>}
          </button>
        ) : (
          <button type="button" onClick={() => setStep((s) => s + 1)} disabled={!canNext}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="weekly-next-btn">
            Next <ArrowRight size={18} strokeWidth={2} />
          </button>
        )}
      </div>
    </section>
  );
}
